import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import Dialog, { DialogProps } from '../Dialog';
import Button from '../Button';
import usePlayerTalents from '../../hooks/usePlayerTalents';
import { useSelector } from 'react-redux';
import { 
  TalentType,
  getTalentDisplayName,
  getTalentIcon,
  calculateTalentEffect
} from '../../types/talents';
import { useAppTranslation } from '../../i18n/useAppTranslation';

interface TalentUpgradeDialogProps extends Omit<DialogProps, 'children'> {
  talentId: TalentType;
  onUpgradeSuccess?: () => void;
}

const TalentUpgradeDialog: React.FC<TalentUpgradeDialogProps> = ({
  open,
  onClose,
  talentId,
  onUpgradeSuccess,
  ...dialogProps
}) => {
  const { t } = useAppTranslation(["research", "common", "village"]);
  const {
    upgradeTalent,
    upgrading,
    getTalentById,
    getAvailableTalentById,
    canUpgradeTalent
  } = usePlayerTalents();

  // Get user resources
  const userInfo = useSelector((state: any) => state.userInfo);

  const playerTalent = getTalentById(talentId);
  const availableTalent = getAvailableTalentById(talentId);

  const talentInfo = useMemo(() => {
    if (!availableTalent) return null;

    const displayName = getTalentDisplayName(talentId);
    const icon = getTalentIcon(talentId);
    const currentLevel = playerTalent?.level || 0;
    const nextLevel = currentLevel + 1;
    const maxLevel = availableTalent.maxLevel;
    const isFirstUnlock = currentLevel === 0;
    const isMaxLevel = currentLevel >= maxLevel;

    return {
      displayName,
      icon,
      currentLevel,
      nextLevel,
      maxLevel,
      isFirstUnlock,
      isMaxLevel,
      currentEffect:
        currentLevel > 0
          ? calculateTalentEffect(talentId, currentLevel)
          : t("research:talentsUi.none"),
      nextEffect: calculateTalentEffect(talentId, nextLevel),
      cost: availableTalent.cost,
      requirements: availableTalent.requirements
    };
  }, [talentId, playerTalent, availableTalent, t]);

  const canAfford = useMemo(() => {
    if (!talentInfo?.cost || !userInfo) return false;
    const { cost } = talentInfo;
    return (
      (!cost.cleg || userInfo.totalCleg >= cost.cleg) &&
      (!cost.bloodGems || userInfo.totalBloodGem >= cost.bloodGems)
    );
  }, [talentInfo, userInfo]);

  const handleUpgrade = async () => {
    if (!canUpgradeTalent(talentId)) return;

    const success = await upgradeTalent(talentId);
    if (success) {
      onUpgradeSuccess?.();
    }
  };

  if (!talentInfo || !open) return null;

  const {
    displayName,
    icon,
    currentLevel,
    nextLevel,
    maxLevel,
    isFirstUnlock,
    isMaxLevel,
    currentEffect,
    nextEffect,
    cost,
    requirements
  } = talentInfo;

  const isUpgrading = upgrading === talentId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        isFirstUnlock
          ? t("research:talentsUi.unlockTalent")
          : t("research:talentsUi.upgradeTalent")
      }
      size="lg"
      noFooter
      {...dialogProps}
    >
      <div className="talent-upgrade-container" style={{
        backgroundColor: '#1a1a1a',
        color: '#ffffff',
        borderRadius: '8px',
        padding: '20px'
      }}>
        {/* Talent Header */}
        <div className="text-center mb-4">
          <img
            src={icon}
            alt={displayName}
            style={{
              width: 80,
              height: 80,
              borderRadius: '12px',
              border: '3px solid rgba(255,255,255,0.2)',
              marginBottom: '1rem'
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/assets/images/talents/default.png';
            }}
          />
          <h4 className="mb-2">{displayName}</h4>
          <div className="text-muted">
            {isFirstUnlock 
              ? t("research:talentsUi.unlockThisTalent")
              : t("research:talentsUi.upgradeFromTo", {
                  current: currentLevel,
                  next: nextLevel,
                })
            }
          </div>
        </div>

        {/* Effect Comparison */}
        <Row className="mb-4">
          <Col md={6}>
            <div className="effect-card p-3 border rounded" style={{
              backgroundColor: 'rgba(108, 117, 125, 0.1)',
              borderColor: 'rgba(108, 117, 125, 0.3)',
              backgroundImage: 'linear-gradient(135deg, rgba(108, 117, 125, 0.05) 0%, rgba(108, 117, 125, 0.15) 100%)'
            }}>
              <h6 className="text-muted mb-2">
                {isFirstUnlock
                  ? t("research:talentsUi.noEffect")
                  : t("research:talentsUi.currentEffect")}
              </h6>
              <div className="fs-5">
                {isFirstUnlock ? (
                  <span className="text-muted">
                    {t("research:talentsUi.none")}
                  </span>
                ) : (
                  <span className="text-info">{currentEffect}</span>
                )}
              </div>
              {!isFirstUnlock && (
                <div className="small text-muted mt-1">
                  {t("common:labels.level")} {currentLevel}
                </div>
              )}
            </div>
          </Col>
          <Col md={6}>
            <div className="effect-card p-3 border rounded" style={{
              backgroundColor: 'rgba(13, 202, 240, 0.1)',
              borderColor: '#0dcaf0',
              backgroundImage: 'linear-gradient(135deg, rgba(13, 202, 240, 0.05) 0%, rgba(13, 202, 240, 0.15) 100%)'
            }}>
              <h6 className="text-primary mb-2">
                {isFirstUnlock
                  ? t("research:talentsUi.unlockEffect")
                  : t("research:talentsUi.newEffect")}
              </h6>
              <div className="fs-5 text-success fw-bold">
                {nextEffect}
              </div>
              <div className="small text-muted mt-1">
                {t("common:labels.level")} {nextLevel}
              </div>
            </div>
          </Col>
        </Row>

        {/* Requirements Check */}
        {requirements && Object.keys(requirements).length > 0 && (
          <div className="mb-4">
            <h6>{t("research:talents.requirements")}:</h6>
            <div className="requirements-list">
              {Object.entries(requirements).map(([key, value]) => (
                <div key={key} className="d-flex justify-content-between align-items-center py-1">
                  <span>{key.replace(/([A-Z])/g, ' $1').toLowerCase()}: {value}</span>
                  <i className="fas fa-check text-success"></i>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cost Display */}
        <div className="mb-4">
          <h6>{t("common:labels.cost")}:</h6>
          <Row className="g-2">
            {cost.cleg && (
              <Col sm={4}>
                <div className={`cost-item p-2 border rounded text-center ${
                  userInfo?.totalCleg >= cost.cleg ? 'border-success text-success' : 'border-danger text-danger'
                }`} style={{
                  backgroundColor: userInfo?.totalCleg >= cost.cleg 
                    ? 'rgba(40, 167, 69, 0.1)' 
                    : 'rgba(220, 53, 69, 0.1)',
                  backgroundImage: userInfo?.totalCleg >= cost.cleg
                    ? 'linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(40, 167, 69, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.15) 100%)'
                }}>
                  <i className="fas fa-certificate me-1"></i>
                  <div className="small">{t("village:resources.cleg")}</div>
                  <div className="fw-bold">{cost.cleg.toLocaleString()}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                    {t("research:talentsUi.have")}:{" "}
                    {userInfo?.totalCleg?.toLocaleString() || 0}
                  </div>
                </div>
              </Col>
            )}
            {cost.bloodGems && (
              <Col sm={4}>
                <div className={`cost-item p-2 border rounded text-center ${
                  userInfo?.totalBloodGem >= cost.bloodGems ? 'border-success text-success' : 'border-danger text-danger'
                }`} style={{
                  backgroundColor: userInfo?.totalBloodGem >= cost.bloodGems 
                    ? 'rgba(40, 167, 69, 0.1)' 
                    : 'rgba(220, 53, 69, 0.1)',
                  backgroundImage: userInfo?.totalBloodGem >= cost.bloodGems
                    ? 'linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(40, 167, 69, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.15) 100%)'
                }}>
                  <i className="fas fa-gem me-1"></i>
                  <div className="small">{t("research:talentsUi.bloodGems")}</div>
                  <div className="fw-bold">{cost.bloodGems.toLocaleString()}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                    {t("research:talentsUi.have")}:{" "}
                    {userInfo?.totalBloodGem?.toLocaleString() || 0}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 d-flex gap-2">
    
            <Button
              variant="wide"
              className="w-100"
              onClick={onClose}
              disabled={isUpgrading}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              variant={isFirstUnlock ? "wide-success" : "wide-primary"}
              className="w-100"
              onClick={handleUpgrade}
              disabled={!canAfford || !canUpgradeTalent(talentId) || isMaxLevel}
              loading={isUpgrading}
            >
              {isFirstUnlock
                ? t("research:talentsUi.unlockTalent")
                : t("research:talentsUi.upgradeTalent")}
            </Button>
        </div>

        {/* Warning Messages */}
        {!canAfford && (
          <div className="alert alert-warning mt-3 mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {t("research:talentsUi.insufficientResources", {
              action: isFirstUnlock
                ? t("research:talentsUi.unlockAction")
                : t("common:actions.upgrade").toLowerCase(),
            })}
          </div>
        )}

        {isMaxLevel && (
          <div className="alert alert-info mt-3 mb-0">
            <i className="fas fa-star me-2"></i>
            {t("research:talentsUi.alreadyMaxLevel")}
          </div>
        )}
      </div>


    </Dialog>
  );
};

export default TalentUpgradeDialog;
